/**
 * CommandPalette zustand store + 鍵盤快捷鍵 hook
 * ──────────────────────────────────────────────
 * - useCommandPaletteStore：跨元件共享 open / setOpen / toggle
 *   * 採 zustand 是為了讓 BaseLayout 的搜尋按鈕、Header 圖示與 Modal 自身
 *     可在無 prop drilling 的前提下共用同一個開關狀態。
 *
 * - useCommandPaletteShortcut：在 BaseLayout 掛 keydown listener
 *   * ⌘K（mac）／ Ctrl+K（win/linux）：toggle palette
 *   * 監聽要在 capture 階段以避免被輸入框 stopPropagation 吃掉，
 *     並在 IME composition 期間（使用注音/拼音輸入時）跳過。
 *
 * 兩個輸出一個檔搞定，避免新增 hooks 目錄並讓 import 路徑短一點。
 */
import { useEffect } from 'react';
import { create } from 'zustand';

/* ════════════════════════════════════════════════════════
 * Store
 * ════════════════════════════════════════════════════════ */

interface CommandPaletteState {
  /** 是否開啟 modal */
  open: boolean;
  /** 直接設定開關狀態 */
  setOpen: (open: boolean) => void;
  /** 切換開關 */
  toggle: () => void;
}

/**
 * 全域 CommandPalette 狀態 store
 * 任何位置都可以呼叫 useCommandPaletteStore.getState().setOpen(true) 主動開啟
 */
export const useCommandPaletteStore = create<CommandPaletteState>((set) => ({
  open: false,
  setOpen: (open) => set({ open }),
  toggle: () => set((s) => ({ open: !s.open })),
}));

/* ════════════════════════════════════════════════════════
 * 快捷鍵 hook
 * ════════════════════════════════════════════════════════ */

/**
 * 在 BaseLayout 內呼叫一次即可，註冊全域 ⌘K / Ctrl+K listener
 * - 與輸入框並存：即使焦點在 input/textarea 也能觸發（這是 palette 的核心特性）
 * - IME composition：composing 期間放行，不攔截「鍵盤輸入字元組合」
 */
export function useCommandPaletteShortcut() {
  const toggle = useCommandPaletteStore((s) => s.toggle);
  const setOpen = useCommandPaletteStore((s) => s.setOpen);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      /* IME composing 時，瀏覽器仍會送 keydown，但這時應跳過避免誤觸 */
      if (e.isComposing) return;

      /* ⌘K / Ctrl+K → toggle palette */
      const isCmdOrCtrlK =
        (e.metaKey || e.ctrlKey) && !e.shiftKey && !e.altKey && e.key.toLowerCase() === 'k';
      if (isCmdOrCtrlK) {
        e.preventDefault();
        e.stopPropagation();
        toggle();
        return;
      }

      /* Esc 關閉（僅當 palette 開啟時；antd Modal 自己會處理 Esc，
         此處作為冗餘保險） */
      if (e.key === 'Escape' && useCommandPaletteStore.getState().open) {
        setOpen(false);
      }
    };

    /* capture 階段 + bubble fallback 雙保險 */
    window.addEventListener('keydown', handler, true);
    return () => window.removeEventListener('keydown', handler, true);
  }, [toggle, setOpen]);
}
